<template>
  <div
    class="messaging-bottom-bar"
    :style="{ height: currentHeight + 'px', transition: isDragging ? 'none' : 'height 0.2s ease' }"
    data-testid="messaging-bottom-bar"
  >
    <!-- Drag resize handle -->
    <div
      class="resize-handle"
      :title="t('Arrastar para redimensionar')"
      @mousedown.prevent="startDrag"
    >
      <div class="resize-handle-grip" />
    </div>

    <!-- Header -->
    <div class="bottom-bar-header" @click="toggle">
      <div class="bottom-bar-header-left">
        <button
          class="bottom-bar-toggle"
          :aria-label="isExpanded ? t('Recolher painel') : t('Expandir painel')"
          data-testid="messaging-bottom-bar-toggle"
          @click.stop="toggle"
        >
          <i :class="isExpanded ? 'pi pi-chevron-down' : 'pi pi-chevron-up'" />
        </button>
        <span class="bottom-bar-title">
          <i class="pi pi-wrench" style="margin-right: 6px;" />
          {{ t('Ferramentas') }}
        </span>
      </div>

      <div class="bottom-bar-header-right" @click.stop>
        <button
          class="bottom-bar-action"
          :title="t('Configurar dispositivos')"
          data-testid="messaging-bottom-bar-settings-btn"
          @click="goToSettings"
        >
          <i class="pi pi-cog" />
        </button>
      </div>
    </div>

    <!-- Expandable Content -->
    <div
      v-show="isExpanded"
      class="bottom-bar-content"
      data-testid="messaging-bottom-bar-content"
    >
      <!-- Tabs -->
      <div class="bottom-bar-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="bottom-bar-tab"
          :class="{ 'is-active': activeTab === tab.id }"
          :data-testid="`messaging-bottom-bar-tab-${tab.id}`"
          @click="activeTab = tab.id"
        >
          {{ t(tab.label) }}
          <span v-if="counts[tab.id] !== null" class="tab-count">{{ counts[tab.id] }}</span>
        </button>
      </div>

      <!-- Tab content -->
      <div class="bottom-bar-tab-content">
        <MessagingBottomBarContatosPendentes
          v-if="activeTab === 'contatos-pendentes'"
          ref="contatosPendentesRef"
          @update:total="counts['contatos-pendentes'] = $event"
        />
        <MessagingBottomBarMensagensProgramadas
          v-if="activeTab === 'mensagens-programadas'"
          ref="mensagensProgramadasRef"
          @update:total="counts['mensagens-programadas'] = $event"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import MessagingBottomBarContatosPendentes from './MessagingBottomBarContatosPendentes.vue';
import MessagingBottomBarMensagensProgramadas from './MessagingBottomBarMensagensProgramadas.vue';

const MIN_HEIGHT = 40;
const SNAP_THRESHOLD = 60;
const TOPBAR_HEIGHT = 60;
const DEFAULT_EXPANDED = Math.round((window.innerHeight - TOPBAR_HEIGHT) / 2);

type TabId = 'contatos-pendentes' | 'clientes-sem-orcamento' | 'mensagens-programadas';

interface TabDef {
  id: TabId;
  label: string;
}

interface ChildTabInstance {
  loadData: () => void;
}

const emit = defineEmits<{
  'height-change': [height: number];
}>();

const { t } = useI18n();
const router = useRouter();

const currentHeight = ref<number>(MIN_HEIGHT);
const lastExpandedHeight = ref<number>(DEFAULT_EXPANDED);
const isDragging = ref<boolean>(false);

const tabs: TabDef[] = [
  { id: 'contatos-pendentes', label: 'Contatos pendentes' },
  { id: 'clientes-sem-orcamento', label: 'Clientes sem orçamento' },
  { id: 'mensagens-programadas', label: 'Mensagens programadas' },
];
const activeTab = ref<TabId>(tabs[0].id);
const counts = ref<Record<TabId, number | null>>({
  'contatos-pendentes': null,
  'clientes-sem-orcamento': null,
  'mensagens-programadas': null,
});
const contatosPendentesRef = ref<ChildTabInstance | null>(null);
const mensagensProgramadasRef = ref<ChildTabInstance | null>(null);

const isExpanded = computed<boolean>(() => currentHeight.value > SNAP_THRESHOLD);

// Load data for the active tab when the panel expands or the tab switches
watch([activeTab, isExpanded], ([tab, expanded]) => {
  if (!expanded) return;
  if (tab === 'contatos-pendentes') {
    contatosPendentesRef.value?.loadData();
  } else if (tab === 'mensagens-programadas') {
    mensagensProgramadasRef.value?.loadData();
  }
}, { flush: 'post' });

// ── Toggle (click) ─────────────────────────────────────────────────────────

const toggle = (): void => {
  if (isExpanded.value) {
    lastExpandedHeight.value = currentHeight.value;
    currentHeight.value = MIN_HEIGHT;
  } else {
    currentHeight.value = lastExpandedHeight.value;
  }
  emit('height-change', currentHeight.value);
};

// ── Drag to resize ─────────────────────────────────────────────────────────

let dragStartY = 0;
let dragStartHeight = 0;

const startDrag = (e: MouseEvent): void => {
  isDragging.value = true;
  dragStartY = e.clientY;
  dragStartHeight = currentHeight.value;
  document.body.style.cursor = 'ns-resize';
  document.body.style.userSelect = 'none';
  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
};

const onDrag = (e: MouseEvent): void => {
  const maxHeight = window.innerHeight - TOPBAR_HEIGHT - 80;
  const delta = dragStartY - e.clientY; // drag up = positive delta = taller
  const newHeight = Math.min(maxHeight, Math.max(MIN_HEIGHT, dragStartHeight + delta));
  currentHeight.value = newHeight;
  emit('height-change', newHeight);
};

const stopDrag = (): void => {
  isDragging.value = false;
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);

  // Snap to collapsed if released below threshold
  if (currentHeight.value < SNAP_THRESHOLD) {
    currentHeight.value = MIN_HEIGHT;
    emit('height-change', MIN_HEIGHT);
  } else {
    lastExpandedHeight.value = currentHeight.value;
  }
};

// ── Lifecycle ──────────────────────────────────────────────────────────────

onMounted(() => {
  emit('height-change', MIN_HEIGHT);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
});

// ── Navigation ─────────────────────────────────────────────────────────────

const goToSettings = (): void => {
  router.push('/devices-franquias');
};
</script>

<style scoped>
.messaging-bottom-bar {
  flex: 0 0 auto;
  background: var(--app-bg-light);
  border-top: 2px solid var(--app-primary-color);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  user-select: none;
  min-height: 40px;
}

/* Resize handle strip */
.resize-handle {
  flex: 0 0 auto;
  height: 6px;
  cursor: ns-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  transition: background 0.15s;
}

.resize-handle:hover {
  background: rgba(var(--app-primary-color-rgb, 230, 184, 0), 0.1);
}

.resize-handle:hover .resize-handle-grip {
  opacity: 1;
  background: var(--app-primary-color);
}

.resize-handle-grip {
  width: 32px;
  height: 3px;
  border-radius: 2px;
  background: var(--app-border-color);
  opacity: 0.8;
  transition: opacity 0.15s, background 0.15s;
  pointer-events: none;
}

/* Header (always visible) */
.bottom-bar-header {
  flex: 0 0 34px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  cursor: pointer;
  height: 34px;
  min-height: 34px;
}

.bottom-bar-header:hover {
  background: rgba(var(--app-primary-color-rgb, 230, 184, 0), 0.06);
}

.bottom-bar-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.bottom-bar-header-right {
  display: flex;
  align-items: center;
  gap: 4px;
}

.bottom-bar-toggle {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--app-text-secondary-color);
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
}

.bottom-bar-toggle:hover {
  color: var(--app-secondary-color);
  background: rgba(var(--app-primary-color-rgb, 230, 184, 0), 0.12);
}

.bottom-bar-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--app-text-color);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.bottom-bar-badge {
  background: var(--app-primary-color);
  color: var(--app-secondary-color);
  font-size: 10px;
  font-weight: 700;
  border-radius: 8px;
  padding: 1px 6px;
  line-height: 16px;
}

.bottom-bar-action {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--app-text-secondary-color);
  display: flex;
  align-items: center;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 13px;
  transition: color 0.15s, background 0.15s;
}

.bottom-bar-action:hover {
  color: var(--app-secondary-color);
  background: rgba(var(--app-primary-color-rgb, 230, 184, 0), 0.1);
}

/* Content area */
.bottom-bar-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-top: 1px solid var(--app-border-color);
}

/* Tabs strip */
.bottom-bar-tabs {
  flex: 0 0 auto;
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--app-border-color);
  background: var(--app-bg-light);
}

.bottom-bar-tab {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  padding: 6px 16px;
  font-size: 12px;
  font-weight: 500;
  color: var(--app-text-secondary-color);
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}

.bottom-bar-tab:hover {
  color: var(--app-text-color);
  background: rgba(var(--app-primary-color-rgb, 230, 184, 0), 0.06);
}

.bottom-bar-tab.is-active {
  color: var(--app-secondary-color);
  font-weight: 600;
  border-bottom-color: var(--app-primary-color);
}

.tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 700;
  background: var(--app-primary-color);
  color: var(--app-secondary-color);
  margin-left: 5px;
  line-height: 1;
}

/* Tab content panel */
.bottom-bar-tab-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px 12px;
  min-height: 0;
}
</style>
